import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Row,
  Table,
  Modal,
  Popconfirm,
  notification,
  Col,
  Input,
  Tooltip,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { DeleteOutlined } from '@ant-design/icons';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import UploadFile from './UploadFile';
import { config } from '../../config';
import { formatFileSize, GET_ALL_FILES, SEARCH_IN_DOCUMENTS_QUERY } from './DataFilesConstants';
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
const Search = Input.Search;

const DataFiles: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { proceedingId } = useParams<any>();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filesToBeDownloaded, setFilesToBeDownloaded] = useState([]);
  const [getAllFiles, { data: allFiles, loading, error, refetch }] = useLazyQuery(GET_ALL_FILES);
  const [keywordForSearch, setKeywordForSearch] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [filesToDisplay, setFilesToDisplay] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchDocumentByContext, { data: searchResult, loading: searching }] =
    useLazyQuery(SEARCH_IN_DOCUMENTS_QUERY);
  useEffect(() => {
    if (tab === '6' && proceedingId) {
      getAllFiles({
        variables: {
          input: proceedingId,
        },
      });
      setFilesToBeDownloaded([]);
      setFilesToDisplay([]);
      setKeywordForSearch(undefined);
      setSelectedRowKeys([]);
    }
  }, [tab]);
  useEffect(() => {
    if (keywordForSearch) {
      setFilesToDisplay(searchResult?.searchDocument?.data);
    } else {
      setFilesToDisplay(allFiles?.dataFiles?.data);
    }
  }, [searchResult, allFiles, keywordForSearch]);
  const handleModalClose = () => {
    refetch();
    setIsModalVisible(false);
    setSelectedFile(null);
    setFilesToBeDownloaded([]);
    setKeywordForSearch(undefined);
    setSelectedRowKeys([]);
  };

  const showModal = (record?: any) => {
    setIsModalVisible(true);
    setSelectedFile(record);
  };

  const handleSearch = (keyword: string) => {
    if (keyword) {
      setKeywordForSearch(keyword);
      searchDocumentByContext({
        variables: {
          input: {
            keyword: keyword,
            proceedingNumber: proceedingId,
          },
        },
      });
    } else {
      setKeywordForSearch(undefined);
      setFilesToDisplay(allFiles?.dataFiles?.data);
    }
  };

  const addToDownloadList = (selected: boolean, exhibitId: string, size: number) => {
    if (selected) {
      filesToBeDownloaded.push({ fileName: `${proceedingId}/${exhibitId}`, size });
      setFilesToBeDownloaded(filesToBeDownloaded);
    } else {
      const newFileList = filesToBeDownloaded.filter(
        (file) => file.fileName !== `${proceedingId}/${exhibitId}`
      );
      setFilesToBeDownloaded(newFileList);
    }
  };
  const allTypes = Array.from(new Set(filesToDisplay?.flatMap((file) => file.type))).map(
    (fileType) => ({ text: fileType, value: fileType })
  );

  const allSubmitters = Array.from(
    new Set(filesToDisplay?.map((file) => file.participantName || file.otherSubmitter))
  ).map((submitter) => ({
    text: submitter,
    value: submitter,
  }));
  const partyName = (record: any) =>
    record.participantId ? record.participantName : record.otherSubmitter;

  const major = (exhibitNumber: string) => exhibitNumber?.split('.')[0];
  const minor = (exhibitNumber: string) => exhibitNumber?.split('.')[1];
  const columns: any = [
    {
      width: 250,
      title: 'Submitter',
      dataIndex: 'participantName',
      filters: allSubmitters,
      onFilter: (value: any, record: any) => {
        return (record.participantName || record.otherSubmitter).toString().indexOf(value) !== -1;
      },
      render: (_: any, record: any) => {
        return record.participantId ? record.participantName : record.otherSubmitter;
      },
      sorter: (a: any, b: any) => partyName(a).localeCompare(partyName(b)),
    },
    {
      width: 250,
      title: 'File Type',
      dataIndex: 'type',
      filters: allTypes,
      onFilter: (value: any, record: any) => {
        return record.type.toString().indexOf(value) !== -1;
      },
      render: (types: string[]) => Array.isArray(types) && types?.join(', '),
    },
    {
      width: 100,
      title: 'Exhibit No',
      dataIndex: 'exhibitNumber',
      render: (_: any, record: any) => {
        return record.originalExhibitFromLegacy
          ? record.originalExhibitFromLegacy
          : record.exhibitNumber;
      },
      sorter: (a: any, b: any) =>
        Number(major(a.exhibitNumber)) - Number(major(b.exhibitNumber)) ||
        Number(minor(a.exhibitNumber)) - Number(minor(b.exhibitNumber)),
    },
    {
      width: 100,
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'name',
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      sorter: (a: any, b: any) => a.fileName.localeCompare(b.fileName),
      render: (fileName: string, record: any) => {
        const filePath = `${proceedingId}/${record.id}`;
        const fileUrl = `${config.REACT_APP_SERVICE_URI}/file/download?attachmentId=${filePath}`;
        return (
          <Button
            style={{ textWrap: 'wrap', height: 'auto', textAlign: 'left' }}
            type="link"
            onClick={() =>
              window.open(
                `${fileUrl}&authorization=${localStorage.getItem('accessToken')}`,
                '_blank'
              )
            }
            icon={<DownloadOutlined />}
          >
            {fileName}
          </Button>
        );
      },
    },
    {
      width: 250,
      title: 'Preview',
      dataIndex: 'fileName',
      key: 'name',
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (fileName: string, record: any) => {
        const filePath = `${proceedingId}/${record.id}`;
        const link = `${
          config.REACT_APP_SERVICE_URI
        }/file/download?attachmentId=${filePath}&authorization=${localStorage.getItem('accessToken')}`;
        const extension = fileName?.split('.').pop();
        return extension?.toLowerCase() === 'pdf' ? (
          <Button color="default" onClick={() => previewDocument(link)} icon={<EyeOutlined />}>
            Preview
          </Button>
        ) : (
          ''
        );
      },
    },
    {
      width: 50,
      title: 'File Size',
      dataIndex: 'size',
      render: (s: any) => {
        return formatFileSize(s);
      },
    },
  ];
  if (isAuthenticated) {
    columns.push(
      {
        width: 150,
        title: 'Confidential Flag',
        dataIndex: 'securityClassification',
        key: 'securityClassification',
        sorter: (a: any, b: any) =>
          a.securityClassification.localeCompare(b.securityClassification),
      },
      {
        width: 250,
        title: 'File Comments',
        dataIndex: 'comments',
        key: 'comments',
      },
      {
        width: 120,
        title: 'Upload Date',
        dataIndex: 'actualRecievedOrSentDate',
        key: 'actualRecievedOrSentDate',
        sorter: (a: any, b: any) => {
          const dateA = a.actualRecievedOrSentDate ? dayjs(a.actualRecievedOrSentDate).unix() : 0;
          const dateB = b.actualRecievedOrSentDate ? dayjs(b.actualRecievedOrSentDate).unix() : 0;
          return dateA - dateB;
        },
      }
    );
    columns.push({
      title: 'Delete',
      key: 'deleteIcon',
      width: 50,
      onCell: () => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
          },
        };
      },
      render: (_: any, exhibit: any) => (
        <Popconfirm
          title="Delete Exhibit"
          description={'Are you sure to delete this Exhibit?'}
          onConfirm={async () => {
            try {
              await axios.delete(
                `${config.REACT_APP_SERVICE_URI}/file/remove?proceedingId=${proceedingId}&attachmentId=${exhibit.fileName}&exhibitId=${exhibit.id}`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: localStorage.getItem('accessToken'),
                  },
                }
              );
              notification.success({ message: 'File Removed' });
              refetch();
            } catch (error) {
              notification.error({
                message: 'File could not be removed',
              });
            }
          }}
          okText="Yes"
          cancelText="No"
        >
          <Button
            icon={<DeleteOutlined />}
            size={'small'}
            style={{ backgroundColor: 'white', color: 'red' }}
          />
        </Popconfirm>
      ),
    });
  }
  if (error) return <p>Error: {error?.message}</p>;

  const previewDocument = (url: string) => {
    setPdfUrl(url);
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
    setPdfUrl(null);
  };

  const downloadFiles = async () => {
    if (filesToBeDownloaded.length < 2) {
      notification.info({
        message:
          'Batch download requires more than 1 exhibit to be selected. Please click on the File Name to download a single exhibit.',
      });
    } else {
      try {
        const fileNames: string[] = [];
        let totalSize = 0;
        filesToBeDownloaded.forEach((file) => {
          fileNames.push(file.fileName);
          totalSize += Number(file.size);
        });
        if (totalSize > 3221225472) {
          notification.error({
            message:
              totalSize +
              'Total size of all selected files exceeds the maximum limit of 3 GB. Please refer to the file sizes and change your selection.',
          });
        } else {
          setDownloading(true);
          const response = await axios.post(
            `${config.REACT_APP_SERVICE_URI}/file/downloadBulk`,
            { fileNames: fileNames },
            {
              headers: {
                Authorization: localStorage.getItem('accessToken'),
              },
              responseType: 'blob',
            }
          );

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'proceedingExhibits.zip');
          document.body.appendChild(link);
          link.click();
          link.remove();
          setDownloading(false);
          setFilesToBeDownloaded([]);
          setSelectedRowKeys([]);
        }
      } catch (error) {
        setDownloading(false);
        console.error('Error downloading the files:', error);
      }
    }
  };

  const handleTableChange = () => {
    setSelectedRowKeys([]);
    setFilesToBeDownloaded([]);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    onSelect: (record: any, selected: boolean, selectedRows: any) => {
      addToDownloadList(selected, record.id, record.size);
    },
    onSelectAll: (selected: any, selectedRows: any, changeRows: any) => {
      if (!selected) {
        setSelectedRowKeys([]);
        setFilesToBeDownloaded([]);
      } else {
        setSelectedRowKeys(selectedRows.map((row: any) => row.key));
        selectedRows.map((row: { id: string; size: number }) => {
          addToDownloadList(selected, row.id, row.size);
        });
      }
    },
  };
  return (
    <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
      <Row>
        {isAuthenticated && (
          <Button
            type="primary"
            onClick={() => showModal()}
            style={{ marginLeft: '10px', marginRight: '10px' }}
          >
            Add New Exhibit
          </Button>
        )}
        <Button type="link" onClick={async () => await downloadFiles()}>
          Download Selected
        </Button>
      </Row>
      <Row>
        <Card
          title={
            <Row justify="space-between" style={{ width: '100%' }}>
              <Col>
                <span className="highlighted-title">Exhibits For this Proceeding</span>
              </Col>
              <Col>
                <div
                  style={{
                    border: '0.5px solid #b0b0b0',
                    padding: '10px',
                    borderRadius: '5px',
                    marginBottom: '3mm',
                    boxShadow: '0 0 3px 1px rgba(176, 176, 176, 0.8)',
                  }}
                >
                  {' '}
                  <Search
                    value={keywordForSearch}
                    placeholder={keywordForSearch}
                    style={{ width: 200 }}
                    onSearch={(value) => handleSearch(value)}
                    allowClear
                  />
                </div>
              </Col>
            </Row>
          }
          style={{ width: '100%', marginTop: '25px' }}
          className="card"
        >
          <Table
            loading={loading || downloading || searching}
            dataSource={filesToDisplay}
            columns={columns}
            rowSelection={{ ...rowSelection }}
            onChange={handleTableChange}
            onRow={(record: any) => ({
              onClick: () => {
                if (isAuthenticated) showModal(record);
              },
              style: { cursor: isAuthenticated ? 'pointer' : 'default', verticalAlign: 'top' },
            })}
          />
        </Card>
      </Row>
      <Modal
        title="Add Or Select Files"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        closable={false}
        destroyOnClose
      >
        <UploadFile
          closeModal={handleModalClose}
          fileDetails={selectedFile}
          mainExhibits={allFiles?.dataFiles?.exhibitNumbers}
        />
      </Modal>
      <Modal open={visible} onCancel={closeModal} footer={null} width={800}>
        {pdfUrl && <Viewer fileUrl={pdfUrl}></Viewer>}
      </Modal>
    </div>
  );
};

export default DataFiles;
